import Router from 'vue-router';
import layout from '@src/layouts/ReferralJam';
import { trackingRoute } from '@src/lib/logs';

export const routes = [
  { path: '/', redirect: '/dashboard' },
  {
    path: '/dashboard',
    name: 'Dashboard',
    components: {
      default: () => import(/* webpackChunkName: "referralJam" */'@/views/Dashboards/ReferralJam'),
      layout,
    },
    meta: {
      // requiresAuth: true,
      headerLinks: [
        { title: 'All Events', to: { name: 'EventList' } },
      ],
    },
  },

  {
    name: 'Login',
    path: '/login',
    components: {
      layout: () => import('@/layouts/Minimal'),
      default: () => import('@/views/Auth/Container'),
      focus: () => import('@/views/Auth/Login'),
    },
  },
  {
    name: 'LoginForgot',
    path: '/login/forgot',
    components: {
      layout: () => import('@/layouts/Minimal'),
      default: () => import('@/views/Auth/Container'),
      focus: () => import('@/views/Auth/LoginForgot'),
    },
    children: [
      { path: ':encodedEmail/:code' },
    ],
  },
  {
    name: 'LoginSSO',
    path: '/login/confirm',
    components: {
      layout: () => import('@/layouts/Minimal'),
      default: () => import('@/views/Auth/Container'),
      focus: () => import('@/views/Auth/SSO'),
    },
    meta: {
      scrollBehavior: false,
    },
  },
  {
    name: 'Register',
    path: '/register',
    components: {
      layout: () => import('@/layouts/Minimal'),
      default: () => import('@/views/Auth/Container'),
      focus: () => import('@/views/Auth/Register'),
    },
  },
  {
    name: 'RegisterInvite',
    path: '/register/invite/:userInviteID',
    components: {
      layout: () => import('@/layouts/Minimal'),
      default: () => import('@/views/Auth/Container'),
      focus: () => import('@/views/Auth/Register'),
    },
    props: { layout: false, default: true, focus: true },
    meta: {
      requiresAuth: false,
      pageView: ({ userInviteID }) => ({ userInvite: { id: userInviteID } }),
    },
  },
  {
    name: 'RegisterVerify',
    path: '/register/verify',
    components: {
      layout: () => import('@/layouts/Minimal'),
      default: () => import('@/views/Auth/Container'),
      focus: () => import('@/views/Auth/RegisterVerify'),
    },
    children: [
      { name: 'RegisterVerifyEmailCode', path: ':encodedEmail/:code' },
    ],
    meta: {
      pageView: () => ({ user: { self: true } }),
    },
  },
  {
    name: 'Logout',
    path: '/logout(/confirm)?',
    components: {
      layout: () => import('@/layouts/Minimal'),
      default: () => import('@/views/Auth/Container'),
      focus: () => import('@/views/Auth/Logout'),
    },
  },

  { name: 'Onboarding', path: '/onboarding', redirect: '/events' },

  {
    name: 'EventList',
    path: '/events',
    components: {
      default: () => import(/* webpackChunkName: "referralJam" */'@/views/ReferralJam/EventList'),
      layout,
    },
    meta: {
      requiresAuth: true,
    },
  },
  {
    name: 'EventPage',
    path: '/events/:eventID',
    props: { default: true, layout: false },
    components: {
      default: () => import(/* webpackChunkName: "referralJam" */'@/views/ReferralJam/EventPage'),
      layout,
    },
  },
  {
    name: 'EventWelcome',
    path: '/events/:eventID/welcome',
    props: { default: true, layout: false },
    components: {
      default: () => import(/* webpackChunkName: "referralJam" */'@/views/ReferralJam/EventWelcome'),
      layout,
    },
  },
  {
    name: 'EventSyncContacts',
    path: '/events/:eventID/sync-contacts',
    redirect({ params }) {
      const { eventID } = params || {};
      return eventID ? { name: 'EventPage', params: { eventID } } : { to: 'Dashboard' };
    },
  },
  {
    name: 'EventRoom',
    path: '/events/:eventID/session',
    props: { default: true, layout: false },
    components: {
      default: () => import(/* webpackChunkName: "referralJam" */'@/views/ReferralJam/EventRoom'),
      layout,
    },
    meta: {
      requiresAuth: true,
      headerLinks: [
        { title: 'Rules', modal: 'event-rules' },
        { title: 'Your Activity', modal: 'event-activity' },
      ],
    },
  },
  {
    name: 'EventSettings',
    path: '/events/:eventID/settings',
    props: { default: true, layout: false },
    components: {
      default: () => import('@/views/ReferralJam/EventSettings'),
      layout,
    },
    meta: {
      requiresAuth: true,
      headerLinks: [
        { title: 'All Events', to: { name: 'EventList' } },
      ],
    },
  },
  {
    name: 'EventJobShareShow',
    path: '/events/:eventID/job-share/:shareCode',
    alias: [ '/e/r/:eventID/:shareCode' ],
    props: { default: true, layout: false },
    component: () => import('@/views/jobs/ShareShow'),
    meta: {
      pageView: ({ eventID, shareCode }) => ({
        event: { id: eventID },
        job: { shareCode },
        user: { shareCode },
      }),
    },
  },
  {
    name: 'EventContentShareShow',
    path: '/events/:eventID/content-share/:shareCode',
    alias: [ '/e/c/:eventID/:shareCode' ],
    props: true,
    component: () => import('@/views/content-share/ShareShow'),
    // props: { default: true, layout: false },
    // components: {
    //   default: () => import('@/views/jobs/ShareShow'),
    //   layout: () => import('@src/layouts/Minimal'),
    // },
    meta: {
      pageView: ({ eventID, shareCode }) => ({
        event: { id: eventID },
        contentShare: { shareCode },
        user: { shareCode },
      }),
    },
  },

  trackingRoute,
  { name: 'Error', path: '/error/:status?', component: () => import('@/views/Error'), props: true },
  { path: '*', component: () => import('@/views/Error'), props: { status: 404 } },
];

export function createRouter() {
  return new Router({
    // base,
    mode: 'history',
    scrollBehavior(to, from, savedPosition) {
      if (savedPosition) {
        return savedPosition;
      } else if (to.hash && !(to.meta && to.meta.scrollBehavior === false)) {
        // Don't jump-to-hash if the page doesn't support this behavior
        return {
          selector: to.hash
          // , offset: { x: 0, y: 10 }
        };
      } else {
        return { x: 0, y: 0 };
      }
    },
    routes,
  });
}



// WEBPACK FOOTER //
// ./src/router/referralJam.js